import type { FocusEvent } from 'react';
import type { Range } from '@tanstack/react-virtual';

import { X, Play, Pause, CircleX, RotateCcw } from 'lucide-react';
import { memo, useMemo, useState, useCallback } from 'react';
import { useVirtualizer, defaultRangeExtractor } from '@tanstack/react-virtual';

import Portal from '@mui/material/Portal';
import { useForkRef } from '@mui/material/utils';
import { useNotificationAvoid } from '@nexus/ari-lib/components/notification-banner';
import { Box, Paper, Stack, Tooltip, IconButton, Typography, LinearProgress } from '@mui/material';

import { isActive, useUploadList, useDocumentsUploads } from './use-documents-uploads';

import type { UploadEntry, UploadStatus } from './use-documents-uploads';

const ROW_HEIGHT_GUESS = 56;
const OVERSCAN = 10;
/** Failed rows stay drawn so the keyboard reaches their Retry. */
const MAX_KEPT_FAILED = 20;

const STATUS_TEXT: Record<Exclude<UploadStatus, 'failed'>, string> = {
  queued: 'Waiting',
  checking: 'Checking',
  sending: 'Uploading',
  finishing: 'Finishing',
  paused: 'Paused',
  done: 'Done',
};

function statusText(entry: UploadEntry): string {
  if (entry.status === 'failed') return entry.error ?? 'Upload failed.';
  const percent = Math.round(entry.progress * 100);
  // A resumed upload only finds its place in storage again, so it has no new percent yet. CLOUD-1160.
  if (entry.resuming && (entry.status === 'queued' || entry.status === 'checking')) {
    return percent > 0 ? `Resuming ${percent}%` : 'Resuming';
  }
  // A paused row keeps the percent it reached. CLOUD-1160.
  const withPercent =
    entry.status === 'checking' || entry.status === 'sending' || (entry.status === 'paused' && percent > 0);
  return withPercent ? `${STATUS_TEXT[entry.status]} ${percent}%` : STATUS_TEXT[entry.status];
}

type RowProps = {
  entry: UploadEntry;
  index: number;
  count: number;
  start: number;
  measure: (node: Element | null) => void;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
};

const UploadRow = memo(function UploadRow({ entry, index, count, start, measure, onCancel, onRetry }: RowProps) {
  const failed = entry.status === 'failed';
  const finished = failed || entry.status === 'done';

  return (
    <Stack
      ref={measure}
      role="listitem"
      aria-setsize={count}
      aria-posinset={index + 1}
      data-index={index}
      data-testid={`cloud.documents.upload-${entry.id}`}
      spacing={0.5}
      sx={{ px: 2, py: 1, position: 'absolute', top: 0, left: 0, width: 1 }}
      style={{ transform: `translateY(${start}px)` }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap title={entry.file.name}>
            {entry.file.name}
          </Typography>
          <Typography variant="caption" color={failed ? 'error.main' : 'text.secondary'}>
            {statusText(entry)}
          </Typography>
        </Box>
        {failed && (
          <Tooltip title="Retry">
            <IconButton size="small" aria-label={`Retry ${entry.file.name}`} onClick={() => onRetry(entry.id)}>
              <RotateCcw size={16} />
            </IconButton>
          </Tooltip>
        )}
        {entry.status !== 'done' && (
          <Tooltip title={failed ? 'Remove' : 'Cancel'}>
            <IconButton
              size="small"
              aria-label={`${failed ? 'Remove' : 'Cancel'} ${entry.file.name}`}
              onClick={() => onCancel(entry.id)}
            >
              <X size={16} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      {!finished && (
        <LinearProgress
          variant={entry.status === 'queued' || entry.status === 'finishing' ? 'indeterminate' : 'determinate'}
          value={entry.progress * 100}
        />
      )}
    </Stack>
  );
});

type Props = {
  uploads: UploadEntry[];
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  onClearFinished: () => void;
  onPauseAll?: () => void;
  onResumeAll?: () => void;
  onCancelAll?: () => void;
};

/** How far the batch is, by bytes, so one large file weighs more than many small ones. CLOUD-1099. */
export function batchProgress(uploads: UploadEntry[]): { done: number; total: number; fraction: number } {
  let bytes = 0;
  let sent = 0;
  let done = 0;
  for (const entry of uploads) {
    const weight = Math.max(entry.file.size, 1);
    const part =
      entry.status === 'done' || entry.status === 'finishing'
        ? 1
        : entry.status === 'sending' ||
          entry.status === 'paused' ||
          (entry.resuming && (entry.status === 'queued' || entry.status === 'checking'))
        ? entry.progress
        : 0;
    if (entry.status === 'done') done += 1;
    bytes += weight;
    sent += weight * part;
  }
  return { done, total: uploads.length, fraction: bytes ? sent / bytes : 0 };
}

function TrayHeader({
  uploads,
  onClearFinished,
  onPauseAll,
  onResumeAll,
  onCancelAll,
}: Omit<Props, 'onCancel' | 'onRetry'>) {
  const busy = uploads.some(isActive);
  const anyPaused = uploads.some((entry) => entry.status === 'paused');
  const open = uploads.some((entry) => entry.status !== 'done');
  const { done, total, fraction } = batchProgress(uploads);
  let title = 'Uploads';
  if (busy) title = 'Uploading';
  else if (anyPaused) title = 'Paused';

  return (
    <Box sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'container.low', px: 2, pt: 1.5, pb: 0.5 }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2">{title}</Typography>
        <Stack direction="row" spacing={0.5}>
          {busy && onPauseAll && (
            <Tooltip title="Pause all">
              <IconButton size="small" aria-label="Pause all uploads" onClick={onPauseAll}>
                <Pause size={16} />
              </IconButton>
            </Tooltip>
          )}
          {/* Paused files can go on while a newer file uploads. CLOUD-1160. */}
          {anyPaused && onResumeAll && (
            <Tooltip title="Resume all">
              <IconButton size="small" aria-label="Resume all uploads" onClick={onResumeAll}>
                <Play size={16} />
              </IconButton>
            </Tooltip>
          )}
          {(busy || anyPaused) && open && onCancelAll && (
            <Tooltip title="Cancel all">
              <IconButton size="small" aria-label="Cancel all uploads" onClick={onCancelAll}>
                <CircleX size={16} />
              </IconButton>
            </Tooltip>
          )}
          {!busy && !anyPaused && (
            <IconButton size="small" aria-label="Close uploads" onClick={onClearFinished}>
              <X size={16} />
            </IconButton>
          )}
        </Stack>
      </Stack>
      {total > 1 && (
        <Stack spacing={0.5} sx={{ pt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" data-testid="cloud.documents.uploads-summary">
            Uploaded {done} of {total} files
          </Typography>
          <LinearProgress variant="determinate" value={fraction * 100} aria-label="Upload progress of all files" />
        </Stack>
      )}
    </Box>
  );
}

function useTrayRows(uploads: UploadEntry[]) {
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);

  const kept = useMemo(() => {
    const indexes = uploads.flatMap((entry, index) => (entry.status === 'failed' ? [index] : []));
    const focused = uploads.findIndex((entry) => entry.id === focusedId);
    return [...indexes.slice(0, MAX_KEPT_FAILED), ...(focused >= 0 ? [focused] : [])];
  }, [uploads, focusedId]);

  const rangeExtractor = useCallback(
    (range: Range) => [...new Set([...defaultRangeExtractor(range), ...kept])].sort((a, b) => a - b),
    [kept]
  );

  const virtualizer = useVirtualizer({
    count: uploads.length,
    getScrollElement: () => scroller,
    estimateSize: () => ROW_HEIGHT_GUESS,
    getItemKey: (index) => uploads[index].id,
    overscan: OVERSCAN,
    rangeExtractor,
  });

  const rowOf = (target: EventTarget) =>
    (target as HTMLElement).closest<HTMLElement>('[role="listitem"]')?.dataset.index;
  const onFocus = (event: FocusEvent) => {
    const index = rowOf(event.target);
    setFocusedId(index === undefined ? null : uploads[Number(index)]?.id ?? null);
  };
  const onBlur = (event: FocusEvent) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusedId(null);
  };

  return { setScroller, virtualizer, onFocus, onBlur };
}

function TrayPanel({ uploads, onCancel, onRetry, ...header }: Props) {
  const { setScroller, virtualizer, onFocus, onBlur } = useTrayRows(uploads);
  const avoidRef = useNotificationAvoid();
  const trayRef = useForkRef<HTMLDivElement>(setScroller, avoidRef);

  return (
    <Portal>
      <Paper
        ref={trayRef}
        data-testid="cloud.documents.upload-tray"
        elevation={8}
        sx={(theme) => ({
          position: 'fixed',
          right: { xs: theme.spacing(1), sm: theme.spacing(3) },
          bottom: { xs: theme.spacing(1), sm: theme.spacing(3) },
          zIndex: theme.zIndex.speedDial,
          width: { xs: `calc(100vw - ${theme.spacing(2)})`, sm: 360 },
          maxHeight: '50vh',
          overflowY: 'auto',
          bgcolor: 'container.low',
        })}
      >
        <TrayHeader uploads={uploads} {...header} />
        <Box
          role="list"
          onFocus={onFocus}
          onBlur={onBlur}
          sx={{ position: 'relative' }}
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualizer.getVirtualItems().map((item) => (
            <UploadRow
              key={item.key}
              entry={uploads[item.index]}
              index={item.index}
              count={uploads.length}
              start={item.start}
              measure={virtualizer.measureElement}
              onCancel={onCancel}
              onRetry={onRetry}
            />
          ))}
        </Box>
      </Paper>
    </Portal>
  );
}

export function DocumentsUploadTray(props: Props) {
  return props.uploads.length ? <TrayPanel {...props} /> : null;
}

export function DocumentsUploadQueueTray() {
  const uploads = useUploadList();
  const actions = useDocumentsUploads();
  if (!actions) return null;
  return (
    <DocumentsUploadTray
      uploads={uploads}
      onCancel={actions.cancel}
      onRetry={actions.retry}
      onClearFinished={actions.clearFinished}
      onPauseAll={actions.pauseAll}
      onResumeAll={actions.resumeAll}
      onCancelAll={actions.cancelAll}
    />
  );
}
